import { isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, PLATFORM_ID, ViewChild, inject } from '@angular/core';

import { RouterLink, RouterLinkActive } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService, AppView, IPublicUser, MessageService } from '@cnt-workspace/data-access';
import { BottomNavVisibilityService } from './bottom-nav-visibility.service';

export { ROUTES_WITH_OWN_ACTION_BAR, hasOwnActionBar } from './bottom-nav-routes';

interface INavSlot {
  label: string;
  icon: string;
  routerLink: string;
  fragment?: string;
  /** When true, render with the unread-messages badge. */
  isMessages?: boolean;
}

const GUEST_SLOTS: INavSlot[] = [
  { label: 'Explore',   icon: 'explore',   routerLink: '/search' },
  { label: 'Favorites', icon: 'favorite',  routerLink: '/wishlists' },
  { label: 'Trips',     icon: 'luggage',   routerLink: '/user-dashboard/trips' },
  { label: 'Inbox',     icon: 'forum',     routerLink: '/user-dashboard/message', isMessages: true },
  { label: 'Account',   icon: 'person',    routerLink: '/user-dashboard/user-profile' },
];

const HOST_SLOTS: INavSlot[] = [
  { label: 'Hosting',   icon: 'dashboard', routerLink: '/hosting' },
  { label: 'Properties',  icon: 'storefront', routerLink: '/properties' },
  { label: 'Inbox',     icon: 'forum',     routerLink: '/user-dashboard/message', isMessages: true },
  { label: 'Earnings',  icon: 'payments',  routerLink: '/hosting', fragment: 'earnings' },
  { label: 'Account',   icon: 'person',    routerLink: '/user-dashboard/user-profile' },
];

@Component({
  selector: 'cnt-bottom-nav',
  standalone: true,
  imports: [RouterLink, RouterLinkActive],
  templateUrl: './bottom-nav.component.html',
})
export class BottomNavComponent implements OnInit, OnDestroy {
  private auth = inject(AuthService);
  private msg = inject(MessageService);
  private navVisibility = inject(BottomNavVisibilityService);
  private platformId = inject(PLATFORM_ID);

  user: IPublicUser | null = null;
  view: AppView = 'guest';
  unreadCount = 0;
  /** Signed in and not on a route with its own bottom action bar (see ROUTES_WITH_OWN_ACTION_BAR). */
  visible = false;
  private subs: Subscription[] = [];
  private unreadSub: Subscription | null = null;
  private navObserver: ResizeObserver | null = null;

  /** The nav bar, when shown. Its height (safe-area padding included, via
   *  offsetHeight) is published as `--cnt-bottom-nav-h` on :root so floating
   *  controls elsewhere (the /search List/Map pill, cookie banner) can sit
   *  above it without guessing its height. Mirrors CookieConsentComponent's
   *  `--cnt-cookie-banner-h` publishing — see that component's `banner`
   *  setter for the same pattern. */
  @ViewChild('nav')
  set nav(ref: ElementRef<HTMLElement> | undefined) {
    this.navObserver?.disconnect();
    this.navObserver = null;
    if (!isPlatformBrowser(this.platformId)) return;
    const el = ref?.nativeElement ?? null;
    if (el && typeof ResizeObserver !== 'undefined') {
      this.navObserver = new ResizeObserver(() => this.publishHeight(el));
      this.navObserver.observe(el);
    }
    this.publishHeight(el);
  }

  private publishHeight(el: HTMLElement | null): void {
    document.documentElement.style.setProperty('--cnt-bottom-nav-h', el ? `${el.offsetHeight}px` : '0px');
  }

  ngOnInit(): void {
    this.subs.push(this.navVisibility.visible$.subscribe((v) => (this.visible = v)));
    this.subs.push(this.auth.currentUser$.subscribe(u => {
      this.user = u;
      this.unreadSub?.unsubscribe();
      this.unreadSub = null;
      this.unreadCount = 0;
      if (u) {
        this.unreadSub = this.msg.unreadFor$(u.email).subscribe(n => (this.unreadCount = n));
      }
    }));
    this.subs.push(this.auth.currentView$.subscribe(v => (this.view = v)));
  }

  ngOnDestroy(): void {
    for (const s of this.subs) s.unsubscribe();
    this.unreadSub?.unsubscribe();
    this.navObserver?.disconnect();
    if (isPlatformBrowser(this.platformId)) this.publishHeight(null);
  }

  get slots(): INavSlot[] {
    return this.view === 'host' ? HOST_SLOTS : GUEST_SLOTS;
  }
}
